import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DainTheme from '../DainTheme';
import { useGetRunPipelineItems } from '../api/MonitoringApi';
import RoutePaths from '../routes/RoutePaths';
import {
    MONITORING_CONTENT_CONTAINER_SX,
    MONITORING_HEADER_CONTAINER_SX,
} from './monitoringLayout';
import RunPipelineItemsList from './RunPipelineItemsList';

function getSourceDetailPath(mediaId, sourceId) {
    return RoutePaths.monitoring.source
        .replace(':mediaId', encodeURIComponent(mediaId))
        .replace(':sourceId', encodeURIComponent(sourceId));
}

function formatDateTime(value, locale) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(locale ?? 'fi-FI', {
        dateStyle: 'short',
        timeStyle: 'short',
    });
}

export default function RunDetail() {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const { mediaId, sourceId, runId } = useParams();
    const runFromState = location.state?.run ?? null;

    const pipelineQuery = useGetRunPipelineItems({ runId });
    const items = pipelineQuery?.data?.result ?? [];

    const startedLabel = formatDateTime(runFromState?.run_started_at, intl.locale);

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 1,
                    }}
                >
                    <Container maxWidth={false} disableGutters sx={MONITORING_HEADER_CONTAINER_SX}>
                        <Stack alignItems="center" spacing={0.5}>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}
                            >
                                {sourceId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {startedLabel
                                    ? intl.formatMessage(
                                          { id: 'monitoring.run.subtitleWithDate' },
                                          { date: startedLabel }
                                      )
                                    : intl.formatMessage({ id: 'monitoring.run.subtitle' })}
                            </Typography>
                        </Stack>
                    </Container>
                </Box>

                <Box
                    sx={{
                        flex: '1 1 auto',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Container
                        maxWidth={false}
                        disableGutters
                        sx={MONITORING_CONTENT_CONTAINER_SX}
                    >
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(getSourceDetailPath(mediaId, sourceId))}
                            sx={{ mb: 1, flexShrink: 0, alignSelf: 'flex-start' }}
                        >
                            {intl.formatMessage({ id: 'monitoring.run.back' })}
                        </Button>

                        {pipelineQuery.isLoading && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.loading' })}
                            </Typography>
                        )}
                        {pipelineQuery.isError && (
                            <Typography variant="body2" color="error" sx={{ my: 2 }}>
                                {intl.formatMessage({ id: 'monitoring.run.error' })}
                            </Typography>
                        )}
                        {!pipelineQuery.isLoading && !pipelineQuery.isError && (
                            <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflow: 'auto' }}>
                                <RunPipelineItemsList items={items} />
                            </Box>
                        )}
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
